import {Injectable} from '@angular/core';
import {Auth, authState, browserSessionPersistence, setPersistence} from '@angular/fire/auth';
import {doc, Firestore, getDoc} from '@angular/fire/firestore';
import {BehaviorSubject, Observable, Subscription} from 'rxjs';
import {AccessFlags, ruleStructure} from '../models-old/utils-old/rule-structure';
import {getStoreAccessFlags} from '../../features/user-access/utils/access.utils';
import {IRuleInfo, IUserAccess} from '../../features/user-access/models';
import {RuleHumanID} from '../../features/user-access/models/rule-info.enum';

@Injectable({
  providedIn: 'root',
})
export class FireAuthService {

  private authStateSub: Subscription;
  private userAccessSubject: BehaviorSubject<IUserAccess> = new BehaviorSubject<IUserAccess>(null);
  private readonly userIdSubject: BehaviorSubject<string>;
  private readonly preLogoutCallbacks: { [name: string]: { call(): void | Promise<void>; promise?: boolean } } = {};
  private readonly ruleFlags = new BehaviorSubject<{ [storeId: string]: boolean[] }>({});

  constructor(
    private firestore: Firestore,
    private auth: Auth,
  ) {
    this.userIdSubject = new BehaviorSubject<string>(null);
    // Move persistence setting to avoid injection context issues
    setTimeout(() => {
      setPersistence(this.auth, browserSessionPersistence).then(() => {
        this.subscribe();
      }).catch((error) => {
        console.error('Failed to set persistence', error);
        this.subscribe();
      });
    }, 0);
  }

  get userIdSub(): Observable<string> {
    return this.userIdSubject.asObservable();
  }

  get userAccess(): Observable<IUserAccess> {
    return this.userAccessSubject.asObservable();
  }

  hasAccess(storeId: string, ruleIdentity: {
    ruleIndex?: number;
    ruleID?: RuleHumanID | AccessFlags
  }): true | IRuleInfo {
    const hasIndex = ruleIdentity.hasOwnProperty('ruleIndex');
    const hasID = ruleIdentity.hasOwnProperty('ruleID');
    let ruleIndex: number = null;
    if (hasID) {
      if (AccessFlags[ruleIdentity.ruleID]) {
        if (this.userAccessSubject.value[ruleIdentity.ruleID]) {
          return true;
        } else if (
          this.userAccessSubject.value.hasOwnProperty(ruleIdentity.ruleID)
          && (
            this.userAccessSubject.value[ruleIdentity.ruleID] === null
            || this.userAccessSubject.value[ruleIdentity.ruleID] === undefined
          )
        ) {
          console.warn(`User should not have an empty access flag ${AccessFlags[ruleIdentity.ruleID]}`);
        }
        return AccessFlags[ruleIdentity.ruleID] as IRuleInfo;
      }
      if (ruleStructure.dictionary.hasOwnProperty(ruleIdentity.ruleID)) {
        const foundRule = ruleStructure.index.find((rule) => rule.humanID === ruleIdentity.ruleID);
        if (foundRule) {
          ruleIndex = foundRule.index;
        }
      }
    }
    if (ruleIndex !== null) {
      if (hasIndex && ruleIndex !== ruleIdentity.ruleIndex) {
        throw Error('Provided ruleIndex and ruleID do not point to the same rule');
      }
    } else if (hasIndex) {
      ruleIndex = ruleIdentity.ruleIndex;

      if (ruleIndex < 0 || ruleIndex >= ruleStructure.index.length) {
        throw Error(`Invalid ruleIndex ${ruleIndex}. No such rule index exists.`);
      }
    } else {
      throw Error('WTF');
    }
    // Shelf Talkers should also be available to users with stock viewer/editor access.
    if (ruleIdentity.ruleID === 'f.' && this.hasShelfTalkersAccessFromStockRules(storeId)) {
      return true;
    }
    if (this.ruleFlags.value[storeId]?.[ruleIndex]) {
      return true;
    } else {
      return ruleStructure.index[ruleIndex];
    }
  }

  private hasShelfTalkersAccessFromStockRules(storeId: string): boolean {
    const storeRules = this.ruleFlags.value?.[storeId];
    if (!storeRules) {
      return false;
    }
    const stockViewRuleIndex = ruleStructure.dictionary['b.']?.index;
    const stockEditRuleIndex = ruleStructure.dictionary['b.i']?.index;
    return Boolean(
      (stockViewRuleIndex !== undefined && storeRules[stockViewRuleIndex]) ||
      (stockEditRuleIndex !== undefined && storeRules[stockEditRuleIndex])
    );
  }

  setLogoutCallback(name: string, callback: () => void | Promise<void>, promise?: boolean): boolean {
    if (this.preLogoutCallbacks.hasOwnProperty(name)) {
      return false;
    }
    this.preLogoutCallbacks[name] = {call: callback};
    if (promise) {
      this.preLogoutCallbacks[name].promise = promise;
    }
    return true;
  }

  private subscribe(): void {
    this.authStateSub = authState(this.auth).subscribe(user => {
      if (user) {
        this.userIdSubject.next(user.email);
        const userDocRef = doc(this.firestore, `user_access/${user.email}`);
        getDoc(userDocRef)
          .then((docSnapshot) => {
            const userAccessData = docSnapshot.exists() ? docSnapshot.data() as IUserAccess : {} as IUserAccess;
            this.setUserAccessFlags(userAccessData);
          })
          .catch(error => {
            console.error('Error fetching user access data:', error);
            this.setUserAccessFlags({} as IUserAccess);
          });
      } else {
        this.userIdSubject.next(null);
      }
    }, error => {
      console.error(error);
    });
  }

  // This has been moved to the new service
  private setUserAccessFlags(access: IUserAccess | null): void {
    if (!access) {
      this.ruleFlags.next(null);
      this.userAccessSubject.next(null);
      return;
    }

    const {storeFlags = {}} = getStoreAccessFlags(access) || {storeFlags: {}};

    this.ruleFlags.next(Object.keys(storeFlags).length ? storeFlags : null);
    this.userAccessSubject.next(access);
  }
}
